import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { terminyMS, terminyZS } from '../../data/terminy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const QuickForm = ({ courseType = '', buttonText = 'Mám zájem', variant = 'default', preselectedTermin = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    participants_count: '',
    preferredTermin: preselectedTermin,
    notes: ''
  });

  // Update preferredTermin when preselectedTermin prop changes
  useEffect(() => {
    if (preselectedTermin) {
      setFormData(prev => ({ ...prev, preferredTermin: preselectedTermin }));
    }
  }, [preselectedTermin]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    
    try {
      const leadData = {
        name: formData.name || 'Neznámé jméno',
        email: formData.email || 'neuvedeno@email.cz',
        phone: formData.phone || null,
        school: formData.school || 'Škola neuvedena',
        participants_count: formData.participants_count || null,
        course_interest: courseType || null,
        message: formData.preferredTermin ? `Preferovaný termín: ${formData.preferredTermin}${formData.notes ? '\n' + formData.notes : ''}` : formData.notes,
        gdpr_consent: true
      };

      // Try to send with retry logic
      let attempts = 0;
      let success = false;
      let lastError = null;

      while (attempts < 3 && !success) {
        try {
          await axios.post(`${API}/leads`, leadData, { timeout: 10000 });
          success = true;
        } catch (err) {
          lastError = err;
          attempts++;
          if (attempts < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
          }
        }
      }

      if (success) {
        setIsSubmitted(true);
        toast.success('Děkujeme! Ozveme se vám.');
      } else {
        throw lastError;
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Still show success to user - data might have been saved
      toast.error('Odeslání trvá déle než obvykle. Zkontrolujeme to.');
      // Log for debugging but don't block user
      console.log('Failed lead data:', { name: formData.name, email: formData.email, school: formData.school });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Děkujeme za váš zájem!</h3>
        <p className="text-slate-600">Ozveme se vám co nejdříve.</p>
      </div>
    );
  }

  const isHero = variant === 'hero';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`grid ${isHero ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-4`}>
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-sm">Jméno *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Jan Novák"
            required
            className="h-11"
          />
        </div>
        
        <div className="space-y-1.5">
          <Label htmlFor="school" className="text-sm">Škola *</Label>
          <Input
            id="school"
            value={formData.school}
            onChange={(e) => handleChange('school', e.target.value)}
            placeholder="ZŠ / MŠ název"
            required
            className="h-11"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm">E-mail *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="jan.novak@skola.cz"
            required
            className="h-11"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-sm">Telefon</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+420 123 456 789"
            className="h-11"
          />
        </div>
      </div>

      {/* Počet pedagogů */}
      <div className="space-y-1.5">
        <Label htmlFor="participants_count" className="text-sm">Počet pedagogů</Label>
        <Input
          id="participants_count"
          type="text"
          value={formData.participants_count}
          onChange={(e) => handleChange('participants_count', e.target.value)}
          placeholder="Např. 5-10 pedagogů"
          className="h-11"
        />
      </div>

      {/* Termín selection for MŠ and ZŠ */}
      {(courseType === 'ms' || courseType === 'zs') && (
        <div className="space-y-1.5">
          <Label htmlFor="termin" className="text-sm">Preferovaný termín (volitelné)</Label>
          <select
            id="termin"
            value={formData.preferredTermin}
            onChange={(e) => handleChange('preferredTermin', e.target.value)}
            className={`w-full h-11 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 ${
              courseType === 'ms' ? 'focus:ring-emerald-500' : 'focus:ring-blue-500'
            }`}
          >
            <option value="">Vyberte termín nebo to uveďte v poznámce</option>
            {(courseType === 'ms' ? terminyMS : terminyZS).map((termin) => (
              <option key={termin.id} value={`#${termin.id} - ${termin.lekce1} a ${termin.lekce2} (${termin.cas} - ${termin.typ})`}>
                #{termin.id} - {termin.lekce1} a {termin.lekce2} ({termin.cas} - {termin.typ})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Notes field */}
      <div className="space-y-1.5">
        <Label htmlFor="notes" className="text-sm">Poznámka (volitelné)</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Zde můžete uvést dodatečné informace, preferenci termínu nebo dotazy..."
          className="min-h-[80px]"
        />
      </div>

      <p className="text-xs text-slate-400 pt-1">
        Odesláním souhlasíte se zpracováním osobních údajů.
      </p>

      <Button 
        type="submit" 
        disabled={isLoading}
        size="lg"
        className={`w-full h-12 text-base ${
          courseType === 'ms' 
            ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/20' 
            : 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-blue-500/20'
        } text-white shadow-lg hover:shadow-xl transition-all`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Odesílám...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            {buttonText}
          </>
        )}
      </Button>
    </form>
  );
};

export default QuickForm;
